import { useQuery } from '@apollo/client';
import { Router } from '@mui/icons-material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { object, string } from 'yup';
import { useUpdateDevice } from '~/api/devices';
import { RouterTabs } from '~/components/RouterTabs';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import { InlineEditTitle, Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { useMoreActions } from '~/hooks/table';
import {
  DeviceLayoutDocument,
  type DeviceLayout__Device as Device,
} from '../../queries/layout.generated';
import { Actions, Menu } from './';

export interface LayoutProps {
  children: ReactNode;
  noPadding?: boolean;
}

export const Layout = ({ children, noPadding = false }: LayoutProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const [save] = useUpdateDevice();

  const location = useLocation();
  const link = useLink();

  const params = useParams<{ deviceId: string }>();
  const deviceId = Number(params.deviceId);

  const { data, error, loading } = useQuery(DeviceLayoutDocument, {
    variables: { deviceId },
  });

  const [moreMenuProps, moreActionProps, moreTableActions] = useMoreActions<Device>();

  if (!error && !loading && data && !data.device) return <Navigate to="/404" replace />;

  const onShowMenu = data?.device ? moreTableActions(data.device).onClick : undefined;

  if (!loading && data?.device == null) return <Navigate to={link('/not-found')} replace />;

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      {data?.device && (
        <>
          <Toolbar
            pageTitle={data.device.name || undefined}
            actions={<Actions device={data.device} onShowMenu={onShowMenu} />}
            titleIcon={<Router />}
            titleText={!data.device.canUpdate.value && data.device.name}
            titleEditor={
              data.device.canUpdate.value && (
                <InlineEditTitle
                  inputId={`devices-item-${data.device.id}-name`}
                  value={data.device.name || ''}
                  update={(value: string) =>
                    save({
                      variables: { deviceId, patch: { name: value.trim() } },
                    })
                  }
                  tooltip={t('pages.devices.show.edit_name_tooltip')}
                  validationSchema={object({
                    name: string().required(t('pages.devices.show.name_required')).trim(),
                  })}
                />
              )
            }
          />
          <Menu
            device={data.device}
            moreMenuProps={moreMenuProps}
            moreActionProps={moreActionProps}
          />
          <nav>
            <RouterTabs
              tabs={[
                {
                  label: t('common.labels.details'),
                  to: { pathname: '../details', search: location.search },
                },
                {
                  label: t('pages.devices.show.tabs.schedule'),
                  to: { pathname: '../schedule', search: location.search },
                },
                {
                  label: t('common.labels.preview'),
                  to: { pathname: '../preview', search: location.search },
                },
                ...(data.device.canUpdate.value
                  ? [
                      {
                        label: t('common.labels.settings'),
                        to: { pathname: '../settings', search: location.search },
                      },
                    ]
                  : []),
                ...(currentNetwork.canManage.value
                  ? [
                      {
                        label: t('pages.devices.show.tabs.services'),
                        to: { pathname: '../services', search: location.search },
                      },
                      {
                        label: t('common.tabs.audit_logs'),
                        to: { pathname: '../audit-logs', search: location.search },
                      },
                      {
                        label: t('pages.devices.show.tabs.command_history'),
                        to: { pathname: '../commands', search: location.search },
                      },
                    ]
                  : []),
              ]}
              variant="scrollable"
            />
          </nav>
          <PageContainer padding={!noPadding}>{children}</PageContainer>
        </>
      )}
    </LoadingPane>
  );
};
